/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Autocomplete,
  Button,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { Stack } from "@mui/system";
import PageGroupFilterCard from "./PageGroupList/PageGroupFilterCard";
import PageGroupCustomTable from "./PageGroupTable/PageGroupCustomTable";
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { selectUserData } from "../../../../features/userSlice";
import { PageGroupEndPointLogic } from "../../../../logic/AssetManagement/PageGroups/pageGroupEndPointLogic";
import SuccessSnackbar from "../../../CommonSuccessSnackbar/SuccessSnackbar";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import Page404 from "../../../Page404/Page403";
import { useDispatch } from "react-redux";
import store from "../../../../store";
import {
  setPageGroupData,
  setSelectedFilterPopups,
  setSelectedFilterTheme,
  setSelectedSharedUid,
  setSelectedSite,
  setSiteOptions,
} from "../../../../features/pageGroupSlice";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SharedStateContext from "../../../SharedStateProvider";
import SimpleBackdrop from "../../../BackDrop/BackDrop";

function WebsitePageGroup(userToken: any) {
  const response = useSelector(selectUserData);
  const [selectedSiteUid] = useState(null) as any;
  const [, setShowPageGroups] = useState(false);
  const [dataShow, setDataShow] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState([]);
  const [selectedPopup, setSelectedPopup] = useState([]);
  const [selectedAbTest, setSelectedAbTest] = useState([]);
  const [selectedPageGroup, setSelectedPageGroup] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [tableRefresh, setTableRefresh] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const { shouldRunEffect, setLoad } = useContext(SharedStateContext);
  const [duplicatePageGroupData, setDuplicatePageGroupData] = useState([]);
  const [search, setSearch] = useState(false);
  const { pageGroupData, selectedSharedUid, selectedSite, siteOptions } =
    useSelector((state: any) => state.pageGroup);
  const [pages, setPages] = useState([]) as any;
  const hasViewPageGroupPermission = CheckPermissions(
    response?.data?.products?.assets,
    "websites",
    "view_page_groups"
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  const dispatch = useDispatch();

  useEffect(() => {
    if (response?.data?.website_permissions) {
      const siteOptions = response.data.website_permissions.map(
        (siteObj: any) => {
          const uid = Object.keys(siteObj)[0]; // UID of the site
          const siteName = siteObj[uid].ui_site_name; // Site name
          return { uid, siteName }; // Return as an object with uid and siteName
        }
      );

      store.dispatch(setSiteOptions(siteOptions)); // Dispatch the memoized options
    }
  }, [response]);

  useEffect(() => {
    if (!selectedSharedUid) {
      setSearch(false);
      setDataShow(false);
    } else if (selectedSite) {
      handleSearch();
    }
    setLoad(false);
    // eslint-disable-next-line
  }, [shouldRunEffect, selectedSharedUid, setLoad]);

  const tableHeadings = {
    group_name: "Name",
    pages_count: "Pages in Group",
    active: "Status",
    themes: "Theme",
    popups: "Popup",
    ab_test: "A/B Test",
    quick_actions: "Quick Actions",
    more_info: "More Info",
  };

  useEffect(() => {
    const siteFromURL = searchParams.get("selectedSite"); // Get from URL
    const updatedParams = new URLSearchParams(searchParams);

    updatedParams.delete("templateSharedUid"); // Remove templateSharedUid from URL

    setSearchParams(updatedParams, { replace: true }); // Update URL without reloading

    if (
      siteFromURL &&
      siteOptions?.find((site: any) => site.uid === siteFromURL)
    ) {
      store.dispatch(setSelectedSharedUid(siteFromURL)); // Set the selected site
      setSearch(true);
      setDataShow(true);
    }
    // eslint-disable-next-line
  }, [siteOptions]);

  const updateURLParams = (siteUid: any) => {
    const updatedParams = new URLSearchParams(window.location.search); // Get existing params

    if (siteUid) {
      updatedParams.set("selectedSite", siteUid); // Update the value
    } else {
      updatedParams.delete("selectedSite"); // Remove key if null
    }

    setSearchParams(updatedParams, { replace: true }); // Update URL without reloading
  };

  const handleSearch = () => {
    if (selectedSharedUid) {
      setSearch(true);
      setDataShow(true);
      store.dispatch(setSelectedSite(selectedSharedUid));
      // Update the URL with selected site's UID
      updateURLParams(selectedSharedUid);
    }
  };

  const handleSelectWebsite = (_: unknown, newValue: any) => {
    clearFilters();
    if (!newValue) {
      store.dispatch(setSelectedSharedUid(null));
      setDataShow(false);
      updateURLParams(null); // Remove selectedSite from URL
    } else {
      store.dispatch(setSelectedSharedUid(newValue.uid));
      setSearch(false);
      store.dispatch(setSelectedSite(null));
      setDataShow(false); // Reset dataShow when a new site is selected
      store.dispatch(setSelectedFilterPopups(null));
      store.dispatch(setSelectedFilterTheme(null));
    }
  };

  useEffect(() => {
    setDataLoaded(false);
    setTableRefresh(false);

    if (search === true && dataShow && selectedSharedUid?.length !== 0) {
      setLoading(true);

      const fetchPageGroups = async () => {
        try {
          await PageGroupEndPointLogic.getAllPageGroupsDuplicate(
            userToken?.userToken?.userToken,
            selectedSharedUid,
            dispatch
          );
          await PageGroupEndPointLogic.getAllPages(
            userToken?.userToken?.userToken,
            selectedSharedUid,
            setPages
          );
        } catch (error) {
          console.error("Error fetching design templates:", error);
        } finally {
          setLoading(false);
          setDataLoaded(true);
        }
      };

      fetchPageGroups();
    }

    // eslint-disable-next-line
  }, [dataShow, selectedSharedUid, tableRefresh]);

  const fetchPageGroups = async () => {
    try {
      setLoading(true);
      setDataLoaded(false);
      await PageGroupEndPointLogic.getAllPageGroupsDuplicate(
        userToken?.userToken?.userToken,
        selectedSharedUid,
        dispatch
      );
      await PageGroupEndPointLogic.getAllPages(
        userToken?.userToken?.userToken,
        selectedSharedUid,
        setPages
      );
    } catch (error) {
      console.error("Error fetching design templates:", error);
    } finally {
      setLoading(false);
      setDataLoaded(true);
    }
  };

  const clearFilters = () => {
    setSearchParams({});
    store.dispatch(setSelectedFilterPopups([]));
    store.dispatch(setSelectedFilterTheme([]));
    store.dispatch(setPageGroupData([]));
  };

  useEffect(() => {
    if (!siteOptions || siteOptions.length === 0) return;

    const searchParams = new URLSearchParams(location.search);
    const selectedSiteFromURL = searchParams.get("selectedSite");

    const isValidUid = siteOptions.some(
      (site: any) => site.uid === selectedSiteFromURL
    );

    if (selectedSiteFromURL && !isValidUid) {
      navigate("/operations/page_groups", { replace: true });
      dispatch(setSelectedSharedUid(null));
    }
  }, [location.search, siteOptions, dispatch, navigate]);

  return (
    <>
      {hasViewPageGroupPermission ? (
        <>
          <Paper sx={{ padding: "8px 16px" }}>
            <Typography fontSize={24}>Page Groups</Typography>
            <Stack
              flexDirection={"row"}
              justifyContent={"space-between"}
              gap={4}
              mt={2}
            >
              <Autocomplete
                sx={{ width: "100%" }}
                getOptionLabel={(option: any) => option.siteName}
                options={siteOptions}
                value={
                  siteOptions.find(
                    (option: any) => option?.uid === selectedSharedUid
                  ) || null
                }
                onChange={handleSelectWebsite}
                onInputChange={(_, __, reason) => {
                  if (reason === "clear") {
                    setShowPageGroups(false); // Set to false when the clear button (cross) is clicked
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search site name or choose from the list"
                    label="Select Website"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ marginLeft: "12px" }} />
                        </InputAdornment>
                      ),
                    }}
                    color="secondary"
                  />
                )}
                size="small"
              />
              <Button
                data-tracker="30"
                variant="contained"
                disabled={!selectedSharedUid}
                sx={{ whiteSpace: "nowrap" }}
                color="secondary"
                onClick={handleSearch}
              >
                SELECT SITE
              </Button>
            </Stack>
          </Paper>
          {dataShow &&
            (loading ? (
              <SimpleBackdrop />
            ) : dataLoaded ? (
              <Paper sx={{ padding: "8px 16px", marginTop: "8px" }}>
                <PageGroupFilterCard
                  setSelectedAbTest={setSelectedAbTest}
                  setSelectedPopup={setSelectedPopup}
                  setSelectedTheme={setSelectedTheme}
                  selectedTheme={selectedTheme}
                  selectedPopup={selectedPopup}
                  selectedAbTest={selectedAbTest}
                  pageGroups={pageGroupData}
                  setSelectedPageGroup={setSelectedPageGroup}
                  selectedPageGroup={selectedPageGroup}
                  selectedSiteUid={selectedSiteUid}
                  setTableRefresh={setTableRefresh}
                  setSnackbarOpen={setSnackbarOpen}
                />
                <PageGroupCustomTable
                  tableHeadings={tableHeadings}
                  fetchPageGroups={fetchPageGroups}
                  pages={pages}
                  pageGroups={pageGroupData}
                  selectedTheme={selectedTheme}
                  selectedPopup={selectedPopup}
                  selectedPageGroup={selectedPageGroup}
                  selectedSiteUid={selectedSiteUid}
                  setTableRefresh={setTableRefresh}
                  setDuplicatePageGroupData={setDuplicatePageGroupData}
                  setSnackbarOpen={setSnackbarOpen}
                  duplicatePageGroupData={duplicatePageGroupData}
                />
              </Paper>
            ) : null)}
        </>
      ) : (
        <Page404 />
      )}
      <SuccessSnackbar
        open={snackbarOpen}
        handleClose={handleSnackbarClose}
        message={""}
      />
    </>
  );
}

export default WebsitePageGroup;
